"use client"

import { useEffect, useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import toast from "react-hot-toast"
import {
  ArrowLeft, IndianRupee, Plus, CalendarDays, RefreshCw,
  Building2, Phone, Mail, MapPin, CheckCircle, Clock, AlertCircle, Download,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { API_ENDPOINTS } from "../../../constants/config"

function fmt(n) {
  return (n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })
}

function fmtDate(d) {
  if (!d) return "—"
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
}

const STATUS_STYLES = {
  paid: "bg-green-100 text-green-700 border border-green-200",
  pending: "bg-yellow-100 text-yellow-700 border border-yellow-200",
  overdue: "bg-red-100 text-red-700 border border-red-200",
}

export default function VendorLedgerDetail() {
  const { vendorId } = useParams()
  const navigate = useNavigate()

  const [vendor, setVendor] = useState(null)
  const [loading, setLoading] = useState(true)
  const [updatingId, setUpdatingId] = useState(null)

  // Add transaction dialog
  const [showAddTxn, setShowAddTxn] = useState(false)
  const [newTxn, setNewTxn] = useState({ date: new Date().toISOString().split("T")[0], amount: "", notes: "", status: "pending" })
  const [savingTxn, setSavingTxn] = useState(false)

  // Pay dialog
  const [payingTxn, setPayingTxn] = useState(null)

  // Download Slip (financial transactions)
  const [showSlipDialog, setShowSlipDialog] = useState(false)
  const [slipFrom, setSlipFrom] = useState("")
  const [slipTo, setSlipTo] = useState("")

  // Billing Slip (SKU-level purchase history)
  const [showBillingDialog, setShowBillingDialog] = useState(false)
  const [billingFrom, setBillingFrom] = useState("")
  const [billingTo, setBillingTo] = useState("")
  const [billingLoading, setBillingLoading] = useState(false)

  const handleDownloadSlip = () => {
    // Vendor transactions carry a single mutable paid_amount (no separate
    // payment-log history like the customer side has), so there's no upfront-
    // vs-later-payment split to do here — each transaction is already one row:
    // Bill = amount, Payment Made = paid_amount, Balance nets both.
    const statusClass = (s) => (s === "paid" ? "PAID" : s === "overdue" ? "OVERDUE" : s === "partial" ? "PARTIAL" : "PENDING")

    const rows = [...(vendor?.transactions || [])]
      .map((t) => ({
        date: new Date(t.date),
        txn: t,
        bill: parseFloat(t.amount) || 0,
        paymentMade: t.status === "paid" ? (parseFloat(t.amount) || 0) : t.status === "partial" ? (parseFloat(t.paid_amount) || 0) : 0,
      }))
      .sort((a, b) => a.date - b.date)

    const fromDate = slipFrom ? new Date(slipFrom) : null
    let toDate = null
    if (slipTo) {
      toDate = new Date(slipTo)
      toDate.setHours(23, 59, 59, 999)
    }

    const before = fromDate ? rows.filter((r) => r.date < fromDate) : []
    const inPeriod = rows.filter((r) => (!fromDate || r.date >= fromDate) && (!toDate || r.date <= toDate))

    const openingBalance = before.reduce((s, r) => s + r.bill - r.paymentMade, 0)
    const dateRange = slipFrom || slipTo ? `${slipFrom || "—"} to ${slipTo || "—"}` : "All time"

    let running = openingBalance
    const bodyRows = inPeriod.map((r) => {
      running += r.bill - r.paymentMade
      return { ...r, balance: running }
    })

    const totalBill = inPeriod.reduce((s, r) => s + r.bill, 0)
    const totalPayment = inPeriod.reduce((s, r) => s + r.paymentMade, 0)
    const closingBalance = running

    const balHtml = (v) => `${fmt(Math.abs(v))}<span class="suffix">${v >= 0 ? "Dr" : "Cr"}</span>`

    const rowsHtml = bodyRows.map((r) => {
      const t = r.txn
      const status = statusClass(t.status)
      return `
        <tr>
          <td class="col-date">${fmtDate(r.date)}</td>
          <td>
            <div class="ref-line">
              <span class="txn-id">${t.id || "—"}</span>
              <span class="status-chip ${status}">${status}</span>
            </div>
            <div class="item-name">${t.notes || "No notes"}</div>
          </td>
          <td class="num muted-dash">—</td>
          <td class="num bill-val">${fmt(r.bill)}</td>
          <td class="num ${r.paymentMade > 0 ? "payment-val" : "muted-dash"}">${r.paymentMade > 0 ? fmt(r.paymentMade) : "—"}</td>
          <td class="num balance-val">${balHtml(r.balance)}</td>
        </tr>`
    }).join("")

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Slip - ${vendor?.name || ""}</title>
      <style>
        :root {
          --ink:#1a2129; --ink-soft:#3a4552; --muted:#64707d; --line:#dcdfe3; --line-soft:#e9ebee;
          --accent:#7c3aed; --accent-soft:#f2ebfe; --paid:#1a7f4b; --paid-soft:#eaf6ef;
          --due:#b4392f; --due-soft:#fbebe9; --partial:#92600b; --partial-soft:#fbf1de;
          --ledger-rule: rgba(124,58,237,0.05); --ledger-margin: rgba(180,57,47,0.28);
        }
        * { box-sizing:border-box; }
        body {
          margin:0; font-family:ui-sans-serif,Arial,sans-serif; color:var(--ink);
          padding:36px 40px 36px 54px;
          background: linear-gradient(var(--ledger-rule) 1px, transparent 1px) 0 150px / 100% 40px, #fff;
          position:relative;
          -webkit-print-color-adjust:exact; print-color-adjust:exact;
        }
        body::before { content:""; position:absolute; top:0; bottom:0; left:38px; width:1px; background:var(--ledger-margin); }
        .masthead { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; padding-bottom:16px; border-bottom:2px solid var(--ink); }
        .slip-tag { display:inline-block; font-size:10.5px; letter-spacing:0.08em; text-transform:uppercase; color:var(--accent); background:var(--accent-soft); border-radius:999px; padding:3px 10px; margin-bottom:8px; font-weight:600; }
        .brand-name { font-family:Georgia,"Times New Roman",serif; font-size:24px; font-weight:600; margin:0 0 4px; }
        .brand-meta { display:flex; flex-wrap:wrap; gap:3px 14px; font-size:12.5px; color:var(--muted); }
        .masthead-right { text-align:right; font-size:11px; color:var(--muted); white-space:nowrap; }
        .masthead-right .period { margin-top:8px; font-size:12px; color:var(--ink-soft); }
        .table-wrap { overflow-x:auto; margin-top:20px; }
        table.ledger { width:100%; border-collapse:collapse; font-size:13px; table-layout:fixed; }
        .ledger thead th { text-align:left; font-size:10.5px; letter-spacing:0.05em; text-transform:uppercase; color:var(--muted); font-weight:700; padding:0 10px 8px; border-bottom:2px solid var(--ink); white-space:nowrap; }
        .ledger th.num, .ledger td.num { text-align:right; }
        .ledger td { padding:10px; border-bottom:1px solid var(--line-soft); vertical-align:top; }
        .ledger tr.opening td { color:var(--muted); font-style:italic; }
        .col-date { white-space:nowrap; color:var(--ink-soft); font-size:12.5px; }
        .ref-line { display:flex; align-items:center; gap:8px; margin-bottom:6px; flex-wrap:wrap; }
        .txn-id { font-family:ui-monospace,"SF Mono",Consolas,monospace; font-size:11px; font-weight:600; color:var(--accent); background:var(--accent-soft); border-radius:4px; padding:1px 6px; }
        .item-name { font-size:12.5px; color:var(--ink-soft); line-height:1.5; }
        .status-chip { font-size:9.5px; font-weight:700; letter-spacing:0.04em; padding:1px 8px; border-radius:999px; text-transform:uppercase; white-space:nowrap; }
        .status-chip.PENDING { background:var(--line-soft); color:var(--muted); border:1px solid var(--line); }
        .status-chip.PARTIAL { background:var(--partial-soft); color:var(--partial); }
        .status-chip.PAID { background:var(--paid-soft); color:var(--paid); }
        .status-chip.OVERDUE { background:var(--due-soft); color:var(--due); }
        .bill-val { color:var(--ink); font-weight:600; }
        .payment-val { color:var(--paid); font-weight:600; }
        .balance-val { font-weight:700; color:var(--due); white-space:nowrap; }
        .balance-val .suffix { font-size:10px; font-weight:600; color:var(--muted); margin-left:2px; }
        .muted-dash { color:var(--muted); }
        .ledger tfoot td { border-top:2px solid var(--ink); border-bottom:none; padding-top:12px; font-weight:700; }
        .ledger tfoot .tot-label { color:var(--muted); font-size:10.5px; letter-spacing:0.05em; text-transform:uppercase; font-weight:700; }
        .closing-total { margin-top:22px; background:var(--ink); color:#fff; border-radius:8px; padding:14px 20px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:16px; }
        .closing-total .group { display:flex; gap:26px; flex-wrap:wrap; }
        .closing-total .item .label { font-size:10px; letter-spacing:0.07em; text-transform:uppercase; opacity:0.7; margin-bottom:2px; }
        .closing-total .item .val { font-size:17px; font-weight:700; }
        .closing-total .item .val.payment { color:#7be0a6; }
        .closing-total .item .val.balance { color:#ff8f85; }
        .footer { text-align:right; font-size:10.5px; color:var(--muted); margin-top:18px; }
        @media print { body{padding:24px 28px 24px 40px;} }
      </style></head><body>
      <div class="masthead">
        <div>
          <div class="slip-tag">Vendor Ledger Statement</div>
          <h1 class="brand-name">${vendor?.name || "Vendor"}</h1>
          <div class="brand-meta">${(vendor?.contactDetails?.phone || vendor?.phone) ? `<span>📞 ${vendor?.contactDetails?.phone || vendor?.phone}</span>` : ""}${(vendor?.contactDetails?.email || vendor?.email) ? `<span>✉ ${vendor?.contactDetails?.email || vendor?.email}</span>` : ""}</div>
        </div>
        <div class="masthead-right">
          Generated ${new Date().toLocaleString("en-IN")}
          <div class="period">Period: ${dateRange}</div>
        </div>
      </div>
      <div class="closing-total">
        <div class="group">
          <div class="item"><div class="label">Total Bill</div><div class="val">₹${fmt(totalBill)}</div></div>
          <div class="item"><div class="label">Total Payment Made</div><div class="val payment">₹${fmt(totalPayment)}</div></div>
        </div>
        <div class="item"><div class="label">Closing Balance</div><div class="val balance">₹${balHtml(closingBalance)}</div></div>
      </div>
      <div class="table-wrap">
      <table class="ledger">
        <thead>
          <tr>
            <th style="width:88px">Date</th>
            <th>Particulars</th>
            <th class="num" style="width:70px">Qty</th>
            <th class="num" style="width:110px">Bill (₹)</th>
            <th class="num" style="width:130px">Payment Made (₹)</th>
            <th class="num" style="width:130px">Balance (₹)</th>
          </tr>
        </thead>
        <tbody>
          <tr class="opening">
            <td class="col-date">${fromDate ? fmtDate(fromDate) : "—"}</td>
            <td>Opening Balance b/f</td>
            <td class="num">—</td>
            <td class="num">—</td>
            <td class="num">—</td>
            <td class="num balance-val">${balHtml(openingBalance)}</td>
          </tr>
          ${rowsHtml || ""}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="3" class="tot-label">Totals</td>
            <td class="num bill-val">${fmt(totalBill)}</td>
            <td class="num payment-val">${fmt(totalPayment)}</td>
            <td class="num balance-val">${balHtml(closingBalance)}</td>
          </tr>
        </tfoot>
      </table>
      </div>
      <div class="footer">Clynox — Vendor Ledger Statement</div>
    </body></html>`
    const win = window.open("", "_blank")
    win.document.write(html)
    win.document.close()
    win.print()
    setShowSlipDialog(false)
  }

  const handleDownloadBillingSlip = async () => {
    try {
      setBillingLoading(true)
      const token = localStorage.getItem("token")
      const params = new URLSearchParams()
      if (billingFrom) params.set("from", billingFrom)
      if (billingTo) params.set("to", billingTo)
      const res = await fetch(`${API_ENDPOINTS.vendorBillingSlip(vendorId)}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error("Failed to fetch billing data")
      const { data: rows } = await res.json()

      // Group rows by date (YYYY-MM-DD)
      const byDate = {}
      rows.forEach((r) => {
        const key = new Date(r.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        if (!byDate[key]) byDate[key] = []
        byDate[key].push(r)
      })

      const totalQty = rows.reduce((s, r) => s + (r.quantity || 0), 0)
      const totalAmt = rows.reduce((s, r) => s + (r.total_cost || 0), 0)
      const dateRange = billingFrom || billingTo ? `${billingFrom || "—"} to ${billingTo || "—"}` : "All time"

      const dateSections = Object.entries(byDate).map(([date, items]) => {
        const itemRows = items.map((r) => `
          <div class="item-line">
            ${r.txn_id ? `<span class="txn-id">TXN #${String(r.txn_id).slice(-6).toUpperCase()}</span>` : ""}
            ${r.sku != null ? `<span class="item-sku">#${r.sku}</span>` : ""}
            <span class="item-name">${r.item_name || "—"}${r.remarks ? ` <span class="item-remark">— ${r.remarks}</span>` : ""}</span>
            <span class="item-leader"></span>
            ${r.quantity != null ? `<span class="item-qty">× ${r.quantity}</span>` : ""}
            <span class="item-amt">₹${fmt(r.total_cost)}</span>
          </div>`).join("")
        return `
          <div class="date-group">
            <div class="date-heading"><span class="d">${date}</span><span class="rule"></span></div>
            <div class="txn-block">${itemRows}</div>
          </div>`
      }).join("")

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Billing Slip — ${vendor?.name || ""}</title>
        <style>
          :root {
            --ink:#1a2129; --ink-soft:#3a4552; --muted:#64707d; --line:#dcdfe3; --line-soft:#e9ebee;
            --accent:#7c3aed; --accent-soft:#f2ebfe;
            --ledger-rule: rgba(124,58,237,0.055); --ledger-margin: rgba(180,57,37,0.28);
          }
          * { box-sizing:border-box; }
          body {
            margin:0; font-family:ui-sans-serif,Arial,sans-serif; color:var(--ink);
            padding:36px 40px 36px 52px;
            background: linear-gradient(var(--ledger-rule) 1px, transparent 1px) 0 118px / 100% 27px, #fff;
            position:relative;
            -webkit-print-color-adjust:exact; print-color-adjust:exact;
          }
          body::before { content:""; position:absolute; top:0; bottom:0; left:36px; width:1px; background:var(--ledger-margin); }
          .masthead { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; padding-bottom:18px; border-bottom:2px solid var(--ink); }
          .slip-tag { display:inline-block; font-size:10.5px; letter-spacing:0.08em; text-transform:uppercase; color:var(--accent); background:var(--accent-soft); border-radius:999px; padding:3px 10px; margin-bottom:8px; font-weight:600; }
          .brand-name { font-family:Georgia,"Times New Roman",serif; font-size:24px; font-weight:600; margin:0 0 4px; }
          .brand-meta { display:flex; flex-wrap:wrap; gap:3px 14px; font-size:12.5px; color:var(--muted); }
          .brand-period { margin-top:6px; font-size:12px; color:var(--ink-soft); }
          .masthead-right { text-align:right; font-size:11px; color:var(--muted); white-space:nowrap; }
          .grand { display:grid; grid-template-columns:repeat(3,1fr); gap:1px; background:var(--line); border:1px solid var(--line); border-radius:8px; overflow:hidden; margin:20px 0 28px; }
          .grand-cell { background:#fff; padding:12px 14px; text-align:center; }
          .grand-label { font-size:10px; letter-spacing:0.06em; text-transform:uppercase; color:var(--muted); margin-bottom:4px; }
          .grand-val { font-size:17px; font-weight:600; }
          .date-group { margin-bottom:18px; }
          .date-group:last-child { margin-bottom:0; }
          .date-heading { display:flex; align-items:baseline; gap:10px; margin-bottom:10px; }
          .date-heading .d { font-size:12.5px; font-weight:700; letter-spacing:0.03em; color:var(--accent); text-transform:uppercase; }
          .date-heading .rule { flex:1; height:1px; background:var(--line); }
          .txn-block { border:1px solid var(--line); border-radius:8px; overflow:hidden; }
          .item-line { display:flex; align-items:baseline; gap:8px; padding:8px 14px; font-size:13.5px; border-bottom:1px dashed var(--line-soft); }
          .item-line:last-of-type { border-bottom:none; }
          .txn-id { font-family:ui-monospace,"SF Mono",Consolas,monospace; font-size:11px; font-weight:600; color:var(--accent); background:var(--accent-soft); border-radius:4px; padding:1px 6px; }
          .item-sku { font-size:10.5px; color:var(--muted); background:var(--line-soft); border-radius:4px; padding:1px 5px; }
          .item-name { color:var(--ink-soft); }
          .item-remark { color:var(--muted); font-style:italic; font-size:12px; }
          .item-leader { flex:1; border-bottom:1px dotted var(--line); transform:translateY(-3px); min-width:12px; }
          .item-qty { font-size:12.5px; color:var(--muted); white-space:nowrap; }
          .item-amt { font-size:12.5px; font-weight:600; color:var(--ink); white-space:nowrap; min-width:72px; text-align:right; }
          .closing-total { margin-top:26px; background:var(--ink); color:#fff; border-radius:8px; padding:16px 20px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:16px; }
          .closing-total .group { display:flex; gap:28px; flex-wrap:wrap; }
          .closing-total .item .label { font-size:10px; letter-spacing:0.07em; text-transform:uppercase; opacity:0.7; margin-bottom:2px; }
          .closing-total .item .val { font-size:20px; font-weight:700; }
          .footer { text-align:right; font-size:10.5px; color:var(--muted); margin-top:18px; }
          .empty { text-align:center; color:var(--muted); padding:40px 0; }
          @media print { body{padding:24px 28px 24px 40px;} }
        </style></head><body>
        <div class="masthead">
          <div>
            <div class="slip-tag">Billing Slip · SKU-level</div>
            <h1 class="brand-name">${vendor?.name || "Vendor"}</h1>
            <div class="brand-meta">${vendor?.contactDetails?.phone ? `<span>📞 ${vendor.contactDetails.phone}</span>` : ""}${vendor?.contactDetails?.email ? `<span>✉ ${vendor.contactDetails.email}</span>` : ""}</div>
            <div class="brand-period">Period: ${dateRange}</div>
          </div>
          <div class="masthead-right">Generated<br>${new Date().toLocaleString("en-IN")}</div>
        </div>
        <div class="grand">
          <div class="grand-cell"><div class="grand-label">Total SKUs</div><div class="grand-val">${rows.length}</div></div>
          <div class="grand-cell"><div class="grand-label">Total Qty</div><div class="grand-val">${totalQty}</div></div>
          <div class="grand-cell"><div class="grand-label">Grand Total</div><div class="grand-val">₹${fmt(totalAmt)}</div></div>
        </div>
        ${rows.length > 0 ? `<div class="closing-total">
          <div class="group">
            <div class="item"><div class="label">Total Qty</div><div class="val">${totalQty}</div></div>
          </div>
          <div class="item"><div class="label">Grand Total</div><div class="val">₹${fmt(totalAmt)}</div></div>
        </div>` : ""}
        ${dateSections || "<div class='empty'>No purchase records in selected range</div>"}
        <div class="footer">Clynox — Vendor Inventory Billing Slip</div>
      </body></html>`

      const win = window.open("", "_blank")
      win.document.write(html)
      win.document.close()
      win.print()
      setShowBillingDialog(false)
    } catch {
      toast.error("Failed to generate billing slip")
    } finally {
      setBillingLoading(false)
    }
  }

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      // Try single vendor endpoint first, fall back to list search
      let vendorData = null
      try {
        const res = await fetch(API_ENDPOINTS.vendor(vendorId), {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.ok) {
          const data = await res.json()
          vendorData = data.data || data
        }
      } catch {
        // ignore, try list fallback below
      }
      if (!vendorData || !vendorData.name) {
        const listRes = await fetch(API_ENDPOINTS.vendors, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!listRes.ok) throw new Error("Failed to fetch vendors")
        const listData = await listRes.json()
        const list = listData.data || []
        vendorData = list.find(v => String(v.id) === String(vendorId) || String(v._id) === String(vendorId)) || null
      }
      setVendor(vendorData)
    } catch {
      toast.error("Failed to load vendor details")
    } finally {
      setLoading(false)
    }
  }, [vendorId])

  useEffect(() => { load() }, [load])

  const handleAddTransaction = async () => {
    const amount = parseFloat(newTxn.amount)
    if (!Number.isFinite(amount) || amount <= 0) { toast.error("Enter a valid amount"); return }
    try {
      setSavingTxn(true)
      const token = localStorage.getItem("token")
      const res = await fetch(API_ENDPOINTS.vendorTransactions(vendorId), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...newTxn, amount }),
      })
      if (!res.ok) throw new Error("Failed to add transaction")
      toast.success("Transaction added")
      setShowAddTxn(false)
      setNewTxn({ date: new Date().toISOString().split("T")[0], amount: "", notes: "", status: "pending" })
      load()
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add transaction")
    } finally {
      setSavingTxn(false)
    }
  }

  const markAsPaid = async (txn) => {
    if (!vendor) return
    try {
      setUpdatingId(txn.id || txn._id)
      const token = localStorage.getItem("token")
      const updatedTransactions = (vendor.transactions || []).map(t =>
        (t.id || t._id) === (txn.id || txn._id) ? { ...t, status: "paid" } : t
      )
      const res = await fetch(API_ENDPOINTS.vendor(vendorId), {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...vendor, transactions: updatedTransactions }),
      })
      if (!res.ok) throw new Error("Failed to update")
      toast.success("Marked as paid")
      setPayingTxn(null)
      load()
    } catch {
      toast.error("Failed to mark as paid")
    } finally {
      setUpdatingId(null)
    }
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  if (!vendor) {
    return (
      <div className="p-6 text-center text-gray-400 text-sm">
        Vendor not found.
        <Button variant="link" onClick={() => navigate("/dashboard/customer-ledger")}>Go back</Button>
      </div>
    )
  }

  const transactions = vendor.transactions || []
  const totalPurchased = transactions.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0)
  const totalPaid = transactions.reduce((s, t) => {
    if (t.status === "paid") return s + (parseFloat(t.amount) || 0)
    if (t.status === "partial") return s + (parseFloat(t.paid_amount) || 0)
    return s
  }, 0)
  const totalPending = transactions.reduce((s, t) => {
    if (t.status === "paid") return s
    if (t.status === "partial") return s + Math.max(0, (parseFloat(t.amount) || 0) - (parseFloat(t.paid_amount) || 0))
    return s + (parseFloat(t.amount) || 0)
  }, 0)
  const phone = vendor.contactDetails?.phone || vendor.phone || ""
  const email = vendor.contactDetails?.email || vendor.email || ""
  const address = vendor.contactDetails?.address || vendor.address || ""

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl mx-auto">
      {/* Back + Header */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard/customer-ledger")} className="mt-0.5 shrink-0">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="bg-purple-100 rounded-lg p-1.5 shrink-0">
              <Building2 className="w-4 h-4 text-purple-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 truncate">{vendor.name}</h1>
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium shrink-0">B2B</span>
          </div>
          <div className="flex flex-wrap gap-3 mt-1.5 text-sm text-gray-500">
            {phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{phone}</span>}
            {email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{email}</span>}
            {address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{address}</span>}
            {vendor.businessType && <span className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-0.5 rounded">{vendor.businessType}</span>}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowSlipDialog(true)} className="flex items-center gap-1.5 text-purple-700 border-purple-300 hover:bg-purple-50">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Ledger Slip</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowBillingDialog(true)} className="flex items-center gap-1.5 text-purple-900 border-purple-400 hover:bg-purple-50 font-medium">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Billing Slip</span>
          </Button>
          <Button size="sm" className="bg-purple-600 hover:bg-purple-700 flex items-center gap-1.5" onClick={() => setShowAddTxn(true)}>
            <Plus className="w-4 h-4" />
            Add Transaction
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500 font-medium mb-1">Total Purchased</p>
          <p className="text-xl font-bold text-gray-900">₹{fmt(totalPurchased)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{transactions.length} transaction{transactions.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500 font-medium mb-1">Total Paid</p>
          <p className="text-xl font-bold text-green-600">₹{fmt(totalPaid)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{transactions.filter(t => t.status === "paid").length} cleared</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500 font-medium mb-1">Outstanding (You Owe)</p>
          <p className="text-xl font-bold text-red-600">₹{fmt(totalPending)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{transactions.filter(t => t.status !== "paid").length} pending</p>
        </div>
      </div>

      {/* Transactions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-800">Transactions ({transactions.length})</h2>
          {totalPending > 0 && (
            <span className="text-xs text-red-600 font-medium bg-red-50 px-2.5 py-1 rounded-full">
              ₹{fmt(totalPending)} outstanding
            </span>
          )}
        </div>

        {transactions.length === 0 ? (
          <div className="bg-white rounded-xl border py-12 text-center text-sm text-gray-400">
            No transactions yet. Add a transaction to track payments to this vendor.
          </div>
        ) : (
          [...transactions].sort((a, b) => new Date(b.date) - new Date(a.date)).map((txn, idx) => {
            const txnId = txn.id || txn._id || idx
            const isPaid = txn.status === "paid"
            return (
              <div key={txnId} className="bg-white rounded-xl border p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[txn.status] || STATUS_STYLES.pending}`}>
                        {isPaid ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                        {(txn.status || "PENDING").toUpperCase()}
                      </span>
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-medium">B2B</span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-gray-400">Amount</p>
                        <p className="font-semibold text-gray-800">₹{fmt(txn.amount)}</p>
                      </div>
                      {txn.status === "partial" && (
                        <>
                          <div>
                            <p className="text-xs text-gray-400">Paid</p>
                            <p className="font-semibold text-green-600">₹{fmt(parseFloat(txn.paid_amount) || 0)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">Due</p>
                            <p className="font-semibold text-red-500">₹{fmt(Math.max(0, (parseFloat(txn.amount) || 0) - (parseFloat(txn.paid_amount) || 0)))}</p>
                          </div>
                        </>
                      )}
                      <div>
                        <p className="text-xs text-gray-400">Date</p>
                        <p className="font-semibold text-gray-700">{fmtDate(txn.date)}</p>
                      </div>
                      {txn.deliveryOnTime !== undefined && txn.status !== "partial" && (
                        <div>
                          <p className="text-xs text-gray-400">Delivery</p>
                          <p className={`font-semibold ${txn.deliveryOnTime ? "text-green-600" : "text-red-500"}`}>
                            {txn.deliveryOnTime ? "On Time" : "Delayed"}
                          </p>
                        </div>
                      )}
                    </div>

                    {txn.notes && <p className="text-xs text-gray-500 italic">{txn.notes}</p>}
                  </div>

                  {!isPaid && (
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-1.5 shrink-0"
                      onClick={() => setPayingTxn(txn)}
                      disabled={updatingId === (txn.id || txn._id)}
                    >
                      {updatingId === (txn.id || txn._id)
                        ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        : <IndianRupee className="w-3.5 h-3.5" />}
                      Mark Paid
                    </Button>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Add Transaction Dialog */}
      <Dialog open={showAddTxn} onOpenChange={setShowAddTxn}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Purchase Transaction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount (₹) *</Label>
              <div className="relative mt-1">
                <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input
                  type="number"
                  min="0"
                  className="pl-7"
                  placeholder="0.00"
                  value={newTxn.amount}
                  onChange={(e) => setNewTxn({ ...newTxn, amount: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Date</Label>
              <Input
                type="date"
                className="mt-1"
                value={newTxn.date}
                onChange={(e) => setNewTxn({ ...newTxn, date: e.target.value })}
              />
            </div>

            <div>
              <Label>Payment Status</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {[
                  { value: "pending", label: "Pending / Unpaid", icon: AlertCircle, active: "bg-yellow-500 text-white border-yellow-500", inactive: "border-gray-200 text-gray-600 hover:border-yellow-400" },
                  { value: "paid", label: "Paid", icon: CheckCircle, active: "bg-green-600 text-white border-green-600", inactive: "border-gray-200 text-gray-600 hover:border-green-400" },
                ].map(opt => {
                  const Icon = opt.icon
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setNewTxn({ ...newTxn, status: opt.value })}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 transition-all text-sm font-medium ${newTxn.status === opt.value ? opt.active : opt.inactive}`}
                    >
                      <Icon className="w-4 h-4" />
                      {opt.label}
                    </button>
                  )
                })}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Input
                className="mt-1"
                placeholder="Optional notes"
                value={newTxn.notes}
                onChange={(e) => setNewTxn({ ...newTxn, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTxn(false)}>Cancel</Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleAddTransaction} disabled={savingTxn}>
              {savingTxn ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Transaction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download Slip Dialog */}
      <Dialog open={showSlipDialog} onOpenChange={setShowSlipDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Download Slip</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-500">Double-entry statement — Date, Particulars, Bill, Payment Made, running Balance. Date range select karo ya blank chhodo sab transactions ke liye.</p>
            <div className="space-y-1.5">
              <Label>From Date</Label>
              <Input type="date" value={slipFrom} onChange={(e) => setSlipFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>To Date</Label>
              <Input type="date" value={slipTo} onChange={(e) => setSlipTo(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSlipDialog(false)}>Cancel</Button>
            <Button onClick={handleDownloadSlip} className="bg-purple-600 hover:bg-purple-700 flex items-center gap-1.5">
              <Download className="w-4 h-4" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Billing Slip Dialog (SKU-level inventory) */}
      <Dialog open={showBillingDialog} onOpenChange={setShowBillingDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Download Billing Slip</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-500">SKU-wise inventory purchase history for this vendor, grouped by date.</p>
            <div className="space-y-1.5">
              <Label>From Date</Label>
              <Input type="date" value={billingFrom} onChange={(e) => setBillingFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>To Date</Label>
              <Input type="date" value={billingTo} onChange={(e) => setBillingTo(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBillingDialog(false)}>Cancel</Button>
            <Button onClick={handleDownloadBillingSlip} disabled={billingLoading} className="bg-purple-600 hover:bg-purple-700 flex items-center gap-1.5">
              {billingLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Paid Confirm Dialog */}
      <Dialog open={!!payingTxn} onOpenChange={(open) => { if (!open) setPayingTxn(null) }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Mark as Paid</DialogTitle>
          </DialogHeader>
          {payingTxn && (
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Transaction Amount</span>
                  <span className="font-bold text-gray-900">₹{fmt(payingTxn.amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Date</span>
                  <span className="text-gray-700">{fmtDate(payingTxn.date)}</span>
                </div>
                {payingTxn.notes && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Notes</span>
                    <span className="text-gray-700 text-right max-w-48">{payingTxn.notes}</span>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500">
                This will mark the full amount of <strong>₹{fmt(payingTxn.amount)}</strong> as paid to <strong>{vendor.name}</strong>.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayingTxn(null)}>Cancel</Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => markAsPaid(payingTxn)}
              disabled={!!updatingId}
            >
              {updatingId ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
